import { StreakService } from './StreakService';

const PREFIX = 'learning_progress_v1_';
const EVENT_LIMIT = 2000;

export const LEARNING_STEPS = [
  'foundations', 'basics', 'alphabet', 'vocabulary', 'grammar',
  'conversations', 'stories', 'writing', 'listen', 'speak', 'practice', 'tests',
];

const TARGETS = {
  foundations: 3,
  basics: 20,
  alphabet: 12,
  vocabulary: 50,
  grammar: 20,
  conversations: 10,
  stories: 5,
  writing: 30,
  listen: 20,
  speak: 20,
  practice: 20,
  tests: 20,
};

export const DAILY_MISSIONS = [
  { step: 'vocabulary', goal: 5, label: 'Learn 5 words', icon: '📚' },
  { step: 'conversations', goal: 1, label: 'Practice 1 dialogue', icon: '💬' },
  { step: 'listen', goal: 2, label: 'Listen to 2 clips', icon: '🎧' },
  { step: 'speak', goal: 3, label: 'Speak 3 sentences', icon: '🎙️' },
];

export function localDateKey(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

const storageKey = (langPair) => `${PREFIX}${langPair || 'guest'}`;

function emptyState(now = new Date()) {
  return {
    steps: Object.fromEntries(LEARNING_STEPS.map((step) => [step, 0])),
    lifetime: Object.fromEntries(LEARNING_STEPS.map((step) => [step, 0])),
    listeningDays: [],
    events: [],
    activityScores: {},
    daily: { date: localDateKey(now), counts: {} },
  };
}

function read(langPair, now = new Date()) {
  const fallback = emptyState(now);
  let state = fallback;
  try {
    state = {
      ...fallback,
      ...JSON.parse(localStorage.getItem(storageKey(langPair)) || '{}'),
    };
  } catch {}

  state.steps = { ...fallback.steps, ...(state.steps || {}) };
  state.lifetime = { ...fallback.lifetime, ...(state.lifetime || {}) };
  state.listeningDays = Array.isArray(state.listeningDays)
    ? state.listeningDays.filter((day) => typeof day === 'string')
    : [];
  state.events = Array.isArray(state.events)
    ? state.events.filter((event) => (
      event
        && LEARNING_STEPS.includes(event.step)
        && Number.isFinite(Number(event.amount))
        && typeof event.day === 'string'
        && typeof event.occurredAt === 'string'
    ))
    : [];
  state.activityScores = state.activityScores && typeof state.activityScores === 'object'
    ? Object.fromEntries(
      Object.entries(state.activityScores).map(([step, scores]) => [
        step,
        scores && typeof scores === 'object' ? { ...scores } : {},
      ]),
    )
    : {};
  state.daily = state.daily?.date === localDateKey(now)
    ? { date: state.daily.date, counts: { ...(state.daily.counts || {}) } }
    : { date: localDateKey(now), counts: {} };
  return state;
}

function write(langPair, state) {
  localStorage.setItem(storageKey(langPair), JSON.stringify(state));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(
      'learning-progress-changed',
      { detail: { langPair } },
    ));
  }
}

export function masteryFor(percent) {
  if (percent >= 100) return 'Mastered';
  if (percent >= 70) return 'Confident';
  if (percent >= 35) return 'Developing';
  return 'Beginner';
}

export const LearningProgressService = {
  record(
    langPair,
    step,
    amount = 1,
    { occurredAt = new Date(), activityId = null } = {},
  ) {
    if (!LEARNING_STEPS.includes(step) || amount <= 0) {
      return this.snapshot(langPair);
    }

    const eventTime = occurredAt instanceof Date ? occurredAt : new Date(occurredAt);
    if (Number.isNaN(eventTime.getTime())) return this.snapshot(langPair);
    const numericAmount = Math.max(1, Math.trunc(amount));
    const state = read(langPair, eventTime);

    let masteryAmount = numericAmount;
    if (activityId) {
      const scores = { ...(state.activityScores[step] || {}) };
      const previousBest = Number(scores[activityId] || 0);
      masteryAmount = Math.max(0, numericAmount - previousBest);
      if (numericAmount > previousBest) scores[activityId] = numericAmount;
      state.activityScores[step] = scores;
    }

    state.steps[step] = Math.min(
      TARGETS[step],
      Number(state.steps[step] || 0) + masteryAmount,
    );
    state.lifetime[step] = Number(state.lifetime[step] || 0) + numericAmount;

    const day = localDateKey(eventTime);
    if (step === 'listen' && !state.listeningDays.includes(day)) {
      state.listeningDays = [...state.listeningDays, day].sort().slice(-30);
    }
    state.events.push({
      step,
      amount: numericAmount,
      occurredAt: eventTime.toISOString(),
      day,
      ...(activityId ? { activityId } : {}),
    });
    state.events = state.events.slice(-EVENT_LIMIT);

    write(langPair, state);
    Promise.resolve(StreakService.recordActivity()).catch(() => {});
    return this.snapshot(langPair);
  },

  snapshot(langPair, { now = new Date() } = {}) {
    const currentTime = now instanceof Date ? now : new Date(now);
    const state = read(langPair, currentTime);
    const steps = Object.fromEntries(LEARNING_STEPS.map((step) => {
      const percent = Math.min(
        100,
        Math.round((Number(state.steps[step] || 0) / TARGETS[step]) * 100),
      );
      return [step, {
        count: Number(state.steps[step] || 0),
        target: TARGETS[step],
        percent,
        mastery: masteryFor(percent),
      }];
    }));
    const overall = Math.round(
      LEARNING_STEPS.reduce((sum, step) => sum + steps[step].percent, 0)
        / LEARNING_STEPS.length,
    );

    const today = localDateKey(currentTime);
    const dailyCounts = { ...(state.daily.counts || {}) };
    state.events.forEach((event) => {
      if (event.day !== today) return;
      dailyCounts[event.step] = Number(dailyCounts[event.step] || 0)
        + Number(event.amount);
    });
    const missions = DAILY_MISSIONS.map((mission) => ({
      ...mission,
      count: Math.min(mission.goal, Number(dailyCounts[mission.step] || 0)),
      complete: Number(dailyCounts[mission.step] || 0) >= mission.goal,
    }));

    const days = new Set(state.listeningDays || []);
    let listeningStreak = 0;
    const cursor = new Date(currentTime);
    while (days.has(localDateKey(cursor))) {
      listeningStreak += 1;
      cursor.setDate(cursor.getDate() - 1);
    }

    return {
      steps,
      overall,
      mastery: masteryFor(overall),
      missions,
      lifetime: state.lifetime,
      listeningStreak,
    };
  },
};
